<script setup>
import { computed, onMounted, ref } from 'vue';
import GoogleBusinessReviewsAPI from 'dashboard/api/googleBusinessReviews';
import GoogleBusinessProfileAPI from 'dashboard/api/googleBusinessProfile';
import Button from 'dashboard/components-next/button/Button.vue';

// ──────────────────────────────────────────────────────────────
// Review state (unchanged from existing implementation)
// ──────────────────────────────────────────────────────────────
const reviews  = ref([]);
const loading  = ref(true);
const error    = ref('');
const filter   = ref('draft');
const savingId = ref(null);

const filtered = computed(() => reviews.value.filter(review => {
  if (filter.value === 'all') return true;
  if (/^[1-5]$/.test(filter.value)) return review.rating === Number(filter.value);
  return review.reply_status === filter.value;
}));

const load = async () => {
  loading.value = true;
  try {
    reviews.value = (await GoogleBusinessReviewsAPI.get()).data;
    error.value   = '';
  } catch {
    error.value = 'Google review records could not be loaded.';
  } finally {
    loading.value = false;
  }
};

const queueReply = async review => {
  const text = review.google_reply_text?.trim();
  if (!text) {
    error.value = 'Write a reply before adding it to the publishing queue.';
    return;
  }
  savingId.value = review.id;
  try {
    const { data } = await GoogleBusinessReviewsAPI.update(review.id, {
      google_reply_text: text,
      reply_status: 'queued',
      last_error: null,
    });
    Object.assign(review, data);
    error.value = '';
  } catch {
    error.value = 'The reply could not be queued.';
  } finally {
    savingId.value = null;
  }
};

const publishReply = async review => {
  if (!window.confirm('Publish this exact queued reply to Google now? This cannot be undone here.')) return;

  savingId.value = review.id;
  try {
    const { data } = await GoogleBusinessReviewsAPI.publish(review.id);
    Object.assign(review, data);
    error.value = '';
  } catch (err) {
    error.value = err.response?.data?.error || 'Google did not confirm this reply. The draft was preserved.';
    await load();
  } finally {
    savingId.value = null;
  }
};

// ──────────────────────────────────────────────────────────────
// GBP Connection state (new)
// ──────────────────────────────────────────────────────────────
const connection          = ref(null);   // { connected, expires_at, gbp_location_id, gbp_location_name }
const connectionLoading   = ref(false);
const connectionError     = ref('');
const connecting          = ref(false);
const syncing             = ref(false);
const syncResult          = ref(null);
const locations           = ref([]);
const loadingLocations    = ref(false);
const locationError       = ref('');
const showLocationPicker  = ref(false);

const isConnected = computed(() => connection.value?.connected === true);

const checkConnection = async () => {
  connectionLoading.value = true;
  try {
    const { data } = await GoogleBusinessProfileAPI.getConnection();
    connection.value = data;
    connectionError.value = '';

    // Show location picker if connected but no location selected yet
    if (data.connected && !data.gbp_location_id) {
      await fetchLocations();
      showLocationPicker.value = true;
    }
  } catch {
    connectionError.value = 'Could not check Google Business Profile connection status.';
  } finally {
    connectionLoading.value = false;
  }
};

const connectGoogle = async () => {
  connecting.value = true;
  connectionError.value = '';
  try {
    const { data } = await GoogleBusinessProfileAPI.initiateConnect();
    if (data.auth_url) {
      // Redirect to Google consent page in the same window.
      window.location.href = data.auth_url;
    } else {
      connectionError.value = data.error || 'Failed to initiate Google authorization.';
    }
  } catch (e) {
    connectionError.value = e?.response?.data?.error || 'Failed to initiate Google authorization.';
  } finally {
    connecting.value = false;
  }
};

const fetchLocations = async () => {
  loadingLocations.value = true;
  locationError.value = '';
  try {
    const { data } = await GoogleBusinessProfileAPI.getLocations();
    locations.value = data.locations || [];
  } catch {
    locationError.value = 'Could not load Business Profile locations from Google.';
  } finally {
    loadingLocations.value = false;
  }
};

const selectLocation = async loc => {
  try {
    await GoogleBusinessProfileAPI.setLocation({
      gbp_account_id:   loc.gbp_account_id,
      gbp_location_id:  loc.id,
      gbp_location_name: loc.name,
    });
    connection.value = {
      ...connection.value,
      gbp_location_id:   loc.id,
      gbp_location_name: loc.name,
    };
    showLocationPicker.value = false;
    locationError.value = '';
  } catch {
    locationError.value = 'Failed to save the selected location.';
  }
};

const syncReviews = async () => {
  syncing.value  = true;
  syncResult.value = null;
  error.value    = '';
  try {
    const { data } = await GoogleBusinessProfileAPI.syncReviews();
    syncResult.value = data;
    // Reload local reviews from DB after sync
    await load();
  } catch (e) {
    error.value = e?.response?.data?.error || 'Review synchronization failed.';
  } finally {
    syncing.value = false;
  }
};

const disconnect = async () => {
  if (!confirm('Disconnect Google Business Profile? You will need to reconnect to sync reviews again.')) return;
  try {
    await GoogleBusinessProfileAPI.disconnect();
    connection.value = { connected: false };
    syncResult.value = null;
  } catch {
    connectionError.value = 'Failed to disconnect.';
  }
};

// Check URL params for post-OAuth redirect notices
const checkOauthRedirectParams = () => {
  const params = new URLSearchParams(window.location.search);
  if (params.get('gbp_notice')) {
    // Successfully connected – re-check connection status to pick up new credential
    checkConnection();
    // Remove params from URL without reload
    window.history.replaceState({}, '', window.location.pathname);
  }
  if (params.get('gbp_error')) {
    connectionError.value = params.get('gbp_error');
    window.history.replaceState({}, '', window.location.pathname);
  }
};

onMounted(async () => {
  checkOauthRedirectParams();
  await checkConnection();
  await load();
});
</script>

<template>
  <div class="flex h-full flex-col overflow-auto bg-n-background-1 p-6 lg:p-8">
    <header class="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 class="text-2xl font-semibold text-n-slate-12">Google Reviews</h1>
        <p class="mt-1 text-sm text-n-slate-11">Official Business Profile reviews and owner replies</p>
      </div>
      <Button label="Refresh" icon="i-lucide-refresh-cw" slate sm :is-loading="loading" @click="load" />
    </header>

    <!-- ─── Connection panel ─── -->
    <div v-if="connectionLoading" class="mb-6 rounded-2xl border border-n-weak bg-n-alpha-3 p-4 text-sm text-n-slate-11">
      Checking Google Business Profile connection…
    </div>

    <!-- Not connected -->
    <div v-else-if="!isConnected" class="mb-6 rounded-2xl border border-n-amber-5 bg-n-amber-2 p-4 text-sm text-n-amber-11">
      <p class="font-medium">Google Business Profile not connected</p>
      <p class="mt-1">Connect your Google account to sync real reviews into this console.</p>
      <p v-if="connectionError" class="mt-2 text-n-ruby-11">{{ connectionError }}</p>
      <Button
        class="mt-3"
        label="Connect Google Business Profile"
        icon="i-lucide-link"
        :is-loading="connecting"
        @click="connectGoogle"
      />
    </div>

    <!-- Connected -->
    <div v-else class="mb-6 rounded-2xl border border-n-teal-5 bg-n-teal-2 p-4 text-sm text-n-teal-11">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p class="font-medium">
            ✓ Connected to Google Business Profile
            <span v-if="connection.gbp_location_name"> — {{ connection.gbp_location_name }}</span>
          </p>
          <p v-if="!connection.gbp_location_id" class="mt-1 text-n-amber-11">No location selected yet. Pick a location below to enable sync.</p>
          <p v-if="syncResult" class="mt-1">
            Last sync: {{ syncResult.synced }} reviews synced
            <span v-if="syncResult.skipped > 0">, {{ syncResult.skipped }} skipped</span>
          </p>
        </div>
        <div class="flex gap-2 flex-wrap">
          <Button v-if="connection.gbp_location_id" label="Sync Reviews" icon="i-lucide-download-cloud" sm :is-loading="syncing" @click="syncReviews" />
          <Button label="Change location" sm slate @click="fetchLocations().then(() => showLocationPicker = true)" />
          <Button label="Disconnect" sm slate @click="disconnect" />
        </div>
      </div>
      <p v-if="connectionError" class="mt-2 text-n-ruby-11">{{ connectionError }}</p>
    </div>

    <!-- Location picker -->
    <div v-if="showLocationPicker" class="mb-6 rounded-2xl border border-n-weak bg-n-alpha-3 p-4">
      <p class="mb-3 font-medium text-n-slate-12">Select your Business Profile location</p>
      <p v-if="loadingLocations" class="text-sm text-n-slate-11">Loading locations…</p>
      <p v-else-if="locationError" class="text-sm text-n-ruby-11">{{ locationError }}</p>
      <ul v-else-if="locations.length" class="space-y-2">
        <li v-for="loc in locations" :key="loc.id">
          <button
            type="button"
            class="w-full rounded-xl border border-n-weak bg-n-alpha-2 px-4 py-2 text-left text-sm text-n-slate-12 hover:bg-n-alpha-4"
            @click="selectLocation(loc)"
          >
            {{ loc.name }}
            <span class="ml-2 text-xs text-n-slate-9">{{ loc.gbp_account_name }}</span>
          </button>
        </li>
      </ul>
      <p v-else class="text-sm text-n-slate-11">No locations found in your Google Business Profile account.</p>
    </div>

    <!-- Filter tabs (unchanged) -->
    <div class="mb-5 flex flex-wrap gap-2">
      <button v-for="option in ['all', 'draft', 'queued', 'publishing', 'published', 'failed', '1', '2', '3', '4', '5']" :key="option" type="button"
        class="rounded-full border px-3 py-1.5 text-sm capitalize"
        :class="filter === option ? 'border-n-brand bg-n-brand text-white' : 'border-n-weak text-n-slate-11'"
        @click="filter = option">{{ /^[1-5]$/.test(option) ? `${option} star` : option.replace('_', ' ') }}</button>
    </div>

    <p v-if="error" class="mb-5 rounded-xl border border-n-ruby-5 bg-n-ruby-2 p-4 text-sm text-n-ruby-11">{{ error }}</p>
    <p v-if="!loading && !filtered.length" class="rounded-2xl border border-n-weak bg-n-alpha-3 p-8 text-center text-n-slate-11">
      No review records in this view.
      <span v-if="!isConnected">Connect Google Business Profile and sync to fetch real reviews.</span>
      <span v-else-if="!connection.gbp_location_id">Select a Business Profile location, then click Sync Reviews.</span>
      <span v-else>Sync to fetch the latest reviews from Google.</span>
    </p>

    <!-- Review cards (unchanged structure) -->
    <section class="grid gap-4 xl:grid-cols-2">
      <article v-for="review in filtered" :key="review.id" class="rounded-2xl border border-n-weak bg-n-alpha-3 p-5 shadow-sm">
        <div class="flex items-start justify-between gap-4">
          <div>
            <h2 class="font-semibold text-n-slate-12">{{ review.reviewer_name }}</h2>
            <p class="mt-1 text-n-amber-9" :aria-label="`${review.rating} out of 5 stars`">{{ '★'.repeat(review.rating) }}{{ '☆'.repeat(5 - review.rating) }}</p>
          </div>
          <span class="rounded-full bg-n-alpha-2 px-3 py-1 text-xs capitalize text-n-slate-11">{{ review.reply_status.replace('_', ' ') }}</span>
        </div>
        <p class="mt-4 whitespace-pre-wrap text-sm text-n-slate-12">{{ review.comment || 'The reviewer left a rating without written feedback.' }}</p>
        <p class="mt-3 text-xs text-n-slate-9">{{ new Date(review.reviewed_at).toLocaleString('en-IN') }}</p>
        <label class="mt-5 block text-xs font-medium uppercase tracking-wide text-n-slate-10">Official owner reply</label>
        <textarea v-model="review.google_reply_text" rows="4" class="mt-2 w-full rounded-xl border border-n-weak bg-n-alpha-2 p-3 text-sm text-n-slate-12" placeholder="Write the reply that will be published on Google…" />
        <div class="mt-3 flex items-center justify-between gap-3">
          <a v-if="review.google_review_url" :href="review.google_review_url" target="_blank" rel="noreferrer" class="text-sm text-n-brand">View on Google ↗</a>
          <span v-else />
          <div class="flex gap-2">
            <Button label="Queue for approval" sm :is-loading="savingId === review.id" :disabled="['published', 'publishing'].includes(review.reply_status)" @click="queueReply(review)" />
            <Button v-if="review.reply_status === 'queued'" label="Publish approved reply" sm :is-loading="savingId === review.id" @click="publishReply(review)" />
          </div>
        </div>
        <p v-if="review.last_error" class="mt-3 text-xs text-n-ruby-11">{{ review.last_error }}</p>
      </article>
    </section>
  </div>
</template>
